import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  keyframes,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AccountBalance,
  AdminPanelSettings,
  CalendarToday,
  CheckCircle,
  ExpandMore,
  Groups,
  Person,
  Receipt,
  Warning,
} from "@mui/icons-material";
import dayjs from "dayjs";
import VenueHireBooking from "../AgilityClubs/VenueHireBooking";
import { featureFlags } from "../../utils/featureFlags";
import type { BookOneOffSessionResponse } from "../AgilityClubs/types";
import BookableSessionsSection from "./BookableSessionsSection";
import type { ClubMembership, Invoice } from "./types";

const fontFamily =
  "'Fredoka', 'Nunito', 'Comfortaa', 'Quicksand', sans-serif";

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 107, 107, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 107, 107, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 107, 107, 0);
  }
`;

interface ClubMembershipCardProps {
  club: ClubMembership;
  onPayNowClick: (invoices: Invoice[]) => void;
  onSessionBooked: (result: BookOneOffSessionResponse) => void;
  onRefreshMemberships: () => void;
}

const formatDate = (dateString: string) => dayjs(dateString).format("DD/MM/YYYY");

const formatCurrency = (amount: number) => `£${amount.toFixed(2)}`;

const ClubMembershipCard: React.FC<ClubMembershipCardProps> = ({
  club,
  onPayNowClick,
  onSessionBooked,
  onRefreshMemberships,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const unpaidInvoices = useMemo(
    () =>
      (club.training_groups ?? []).flatMap((group) =>
        (group.invoices ?? []).filter((inv) => !inv.paid && !inv.voided_at),
      ),
    [club.training_groups],
  );

  const showMultiPay = unpaidInvoices.length >= 2;
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    const unpaidIds = new Set(unpaidInvoices.map((inv) => inv.id));
    setSelectedIds((prev) => {
      const next = new Set<number>();
      prev.forEach((id) => {
        if (unpaidIds.has(id)) next.add(id);
      });
      return next;
    });
  }, [unpaidInvoices]);

  const toggleSelect = (invoiceId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(invoiceId)) {
        next.delete(invoiceId);
      } else {
        next.add(invoiceId);
      }
      return next;
    });
  };

  const selectedInvoices = unpaidInvoices.filter((inv) =>
    selectedIds.has(inv.id),
  );

  const handlePaySelected = () => {
    if (selectedInvoices.length === 0) return;
    onPayNowClick(selectedInvoices);
  };

  const handlePayAll = () => {
    if (unpaidInvoices.length === 0) return;
    onPayNowClick(unpaidInvoices);
  };

  return (
    <Card
      sx={{
        borderRadius: 4,
        boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
        border: "2px solid",
        borderColor: club.is_admin ? "#E0E0E0" : "#E8F5E9",
        background: "white",
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Box
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "50%",
              p: 2,
              mr: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
            }}
          >
            <Groups sx={{ fontSize: 35, color: "white" }} />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#2D3748",
                fontFamily,
              }}
            >
              {club.name}
            </Typography>
            {club.is_admin && (
              <Chip
                icon={<AdminPanelSettings />}
                label="⭐ Club Admin"
                sx={{
                  mt: 1,
                  background: "linear-gradient(45deg, #FFD93D 30%, #FFC107 90%)",
                  color: "#000",
                  fontWeight: 700,
                  fontFamily,
                  boxShadow: "0 2px 8px rgba(255, 193, 7, 0.4)",
                }}
              />
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 3, borderColor: "#E0E0E0", borderWidth: 1 }} />

        {showMultiPay && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ mb: 3 }}
          >
            <Button
              variant="contained"
              startIcon={<AccountBalance />}
              disabled={selectedInvoices.length === 0}
              onClick={handlePaySelected}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontFamily,
                background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              }}
            >
              Pay selected
              {selectedInvoices.length > 0
                ? ` (${selectedInvoices.length})`
                : ""}
            </Button>
            <Button
              variant="outlined"
              startIcon={<AccountBalance />}
              onClick={handlePayAll}
              sx={{ textTransform: "none", fontWeight: 700, fontFamily }}
            >
              Pay all unpaid
            </Button>
          </Stack>
        )}

        {featureFlags.venueHire && (
          <VenueHireBooking
            clubId={club.id}
            clubName={club.name}
            venueHire={club.venue_hire}
          />
        )}

        {featureFlags.oneOffSessions &&
          (club.bookable_sessions?.length ?? 0) > 0 && (
            <BookableSessionsSection
              sessions={club.bookable_sessions ?? []}
              onBooked={onSessionBooked}
              onRefresh={onRefreshMemberships}
            />
          )}

        {(club.upcoming_training_dates?.length ?? 0) > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                fontWeight: 700,
                color: "#4ECDC4",
                fontFamily,
              }}
            >
              <CalendarToday sx={{ mr: 1, fontSize: 20 }} />
              📅 Next Training Sessions
            </Typography>
            <Grid container spacing={2}>
              {(club.upcoming_training_dates ?? []).map((training, idx) => (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <Paper
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      background:
                        training.days_until <= 2
                          ? "linear-gradient(135deg, #FFF4E6 0%, #FFE8CC 100%)"
                          : "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)",
                      border: "2px solid",
                      borderColor:
                        training.days_until <= 2 ? "#FF9800" : "#4CAF50",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 700,
                        color: training.days_until <= 2 ? "#F57C00" : "#2E7D32",
                        mb: 1,
                        fontFamily,
                      }}
                    >
                      {training.group_name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "#2D3748",
                        fontFamily,
                      }}
                    >
                      {formatDate(training.training_date)}
                    </Typography>
                    <Chip
                      label={
                        training.days_until === 0
                          ? "🎉 Today!"
                          : training.days_until === 1
                            ? "Tomorrow!"
                            : `In ${training.days_until} days`
                      }
                      sx={{
                        mt: 1.5,
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        fontFamily,
                        background:
                          training.days_until <= 2
                            ? "linear-gradient(45deg, #FF9800 30%, #F57C00 90%)"
                            : "linear-gradient(45deg, #4CAF50 30%, #2E7D32 90%)",
                        color: "white",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                      }}
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: "#667eea",
              fontFamily,
              mb: 2,
            }}
          >
            🏃‍♀️ Training Groups
          </Typography>
          {(club.training_groups ?? []).map((group) => (
            <Accordion
              key={group.id}
              sx={{
                mb: 2,
                borderRadius: "12px !important",
                border: "2px solid",
                borderColor: group.is_member ? "#4ECDC4" : "#E0E0E0",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    width: "100%",
                    minWidth: 0,
                    pr: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "1rem",
                      lineHeight: 1.3,
                      fontFamily,
                    }}
                  >
                    {group.group_name}
                  </Typography>
                  {(group.is_member ||
                    group.invoices?.some(
                      (inv) => !inv.paid && !inv.voided_at,
                    )) && (
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {group.is_member && (
                        <Chip
                          label="✓ You're in!"
                          size="small"
                          sx={{
                            background:
                              "linear-gradient(45deg, #4ECDC4 30%, #44A08D 90%)",
                            color: "white",
                            fontWeight: 700,
                            fontFamily,
                          }}
                        />
                      )}
                      {group.invoices?.some(
                        (inv) => !inv.paid && !inv.voided_at,
                      ) && (
                        <Chip
                          icon={<Warning />}
                          label="Payment Due"
                          size="small"
                          sx={{
                            background:
                              "linear-gradient(45deg, #FF6B6B 30%, #EE5A6F 90%)",
                            color: "white",
                            fontWeight: 700,
                            fontFamily,
                            animation: `${pulse} 2s infinite`,
                          }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3, bgcolor: "#FAFAFA" }}>
                {group.invoices?.length ? (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        fontWeight: 700,
                        color: "#FF6B6B",
                        fontFamily,
                      }}
                    >
                      <Receipt sx={{ mr: 1, fontSize: 20 }} />
                      💳 Your Invoices
                    </Typography>
                    <Stack spacing={2}>
                      {(group.invoices ?? []).map((invoice) => {
                        const isVoided = Boolean(invoice.voided_at);
                        const isOverdue =
                          !invoice.paid &&
                          !isVoided &&
                          dayjs(invoice.due_date).isBefore(dayjs(), "day");
                        return (
                          <Paper
                            key={invoice.id}
                            elevation={0}
                            sx={{
                              borderRadius: 3,
                              border: "2px solid",
                              borderColor: isVoided
                                ? "#E0E0E0"
                                : invoice.paid
                                  ? "#E8F5E9"
                                  : isOverdue
                                    ? "#FF6B6B"
                                    : "#FFE0B2",
                              bgcolor: isVoided
                                ? "#F5F5F5"
                                : invoice.paid
                                  ? "#FAFAFA"
                                  : isOverdue
                                    ? "#FFF5F5"
                                    : "#FFFDF7",
                              overflow: "hidden",
                              ...(!invoice.paid &&
                                !isVoided && {
                                  cursor: "pointer",
                                  animation: isOverdue
                                    ? `${pulse} 2s infinite`
                                    : undefined,
                                  "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow:
                                      "0 6px 20px rgba(255,107,107,0.2)",
                                  },
                                }),
                            }}
                            onClick={
                              !invoice.paid && !isVoided
                                ? () => {
                                    if (showMultiPay) {
                                      toggleSelect(invoice.id);
                                    } else {
                                      onPayNowClick([invoice]);
                                    }
                                  }
                                : undefined
                            }
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                px: 2.5,
                                pt: 2.5,
                                pb: 1.5,
                              }}
                            >
                              <Box sx={{ display: "flex", gap: 1, flex: 1 }}>
                                {showMultiPay && !invoice.paid && !isVoided && (
                                  <Checkbox
                                    checked={selectedIds.has(invoice.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => toggleSelect(invoice.id)}
                                    inputProps={{
                                      "aria-label": `Select invoice ${invoice.id}`,
                                    }}
                                  />
                                )}
                                <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontFamily,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                    fontWeight: 600,
                                  }}
                                >
                                  Period
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: "#2D3748",
                                    fontFamily,
                                    mt: 0.25,
                                  }}
                                >
                                  {formatDate(invoice.period_start)}
                                  {" – "}
                                  {formatDate(invoice.period_end)}
                                </Typography>
                              </Box>
                              </Box>
                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: 800,
                                  color: invoice.paid
                                    ? "text.secondary"
                                    : "#2D3748",
                                  fontFamily,
                                  lineHeight: 1,
                                }}
                              >
                                {formatCurrency(invoice.amount)}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                px: 2.5,
                                pb: invoice.paid ? 2.5 : 2,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: isVoided
                                    ? "text.secondary"
                                    : isOverdue
                                      ? "#D32F2F"
                                      : "text.secondary",
                                  fontWeight: isOverdue ? 700 : 400,
                                  fontFamily,
                                }}
                              >
                                {isVoided
                                  ? "Voided · no payment required"
                                  : `${isOverdue ? "⚠️ Overdue · " : "Due "}${formatDate(invoice.due_date)}`}
                              </Typography>
                              {isVoided ? (
                                <Chip
                                  label="Voided"
                                  size="small"
                                  sx={{
                                    bgcolor: "#E0E0E0",
                                    color: "#616161",
                                    fontWeight: 700,
                                    fontFamily,
                                  }}
                                />
                              ) : invoice.paid ? (
                                <Chip
                                  icon={<CheckCircle />}
                                  label="Paid ✓"
                                  size="small"
                                  sx={{
                                    background:
                                      "linear-gradient(45deg, #4CAF50 30%, #2E7D32 90%)",
                                    color: "white",
                                    fontWeight: 700,
                                    fontFamily,
                                  }}
                                />
                              ) : (
                                <Chip
                                  icon={<Warning />}
                                  label={isOverdue ? "Overdue" : "Payment Due"}
                                  size="small"
                                  sx={{
                                    background: isOverdue
                                      ? "linear-gradient(45deg, #FF6B6B 30%, #EE5A6F 90%)"
                                      : "linear-gradient(45deg, #FF9800 30%, #F57C00 90%)",
                                    color: "white",
                                    fontWeight: 700,
                                    fontFamily,
                                  }}
                                />
                              )}
                            </Box>

                            {!invoice.paid && !isVoided && (
                              <Box sx={{ px: 2.5, pb: 2.5 }}>
                                <Button
                                  fullWidth
                                  variant="contained"
                                  size={isMobile ? "large" : "medium"}
                                  startIcon={<AccountBalance />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onPayNowClick([invoice]);
                                  }}
                                  sx={{
                                    background:
                                      "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
                                    fontWeight: 700,
                                    fontFamily,
                                    borderRadius: 2,
                                    py: isMobile ? 1.5 : 1,
                                    fontSize: isMobile ? "1rem" : "0.875rem",
                                    boxShadow:
                                      "0 4px 12px rgba(102, 126, 234, 0.4)",
                                  }}
                                >
                                  Pay Now
                                </Button>
                              </Box>
                            )}
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Box>
                ) : null}

                <Box>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      color: "#4ECDC4",
                      fontFamily,
                    }}
                  >
                    👥 Squad Members ({(group.members ?? []).length})
                  </Typography>
                  <Grid container spacing={2}>
                    {(group.members ?? []).map((member) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        key={member.handler_id}
                      >
                        <Paper
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            display: "flex",
                            alignItems: "center",
                            bgcolor: "white",
                            border: "1px solid #E0E0E0",
                          }}
                        >
                          <Avatar
                            sx={{
                              background:
                                "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              mr: 2,
                              width: 40,
                              height: 40,
                            }}
                          >
                            <Person sx={{ fontSize: 24 }} />
                          </Avatar>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, fontFamily }}
                          >
                            {member.handler_name}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        <Box>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              display: "flex",
              alignItems: "center",
              fontWeight: 700,
              color: "#764ba2",
              fontFamily,
            }}
          >
            <AdminPanelSettings sx={{ mr: 1, fontSize: 20 }} />
            ⭐ Club Leaders
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {club.admins?.map((admin) => (
              <Chip
                key={admin.handler_id}
                avatar={
                  <Avatar
                    sx={{
                      background:
                        "linear-gradient(135deg, #FFD93D 0%, #FFC107 100%)",
                    }}
                  >
                    <Person sx={{ color: "#000", fontSize: 20 }} />
                  </Avatar>
                }
                label={admin.handler_name}
                sx={{
                  px: 2,
                  py: 2,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  fontFamily,
                  border: "2px solid #FFD93D",
                  bgcolor: "#FFFEF7",
                }}
              />
            ))}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ClubMembershipCard;
